import { PlayerStills, RewindStill, StillsResolutionConfig, Storyboard } from 'types/recordings';

export type RewindsStillStoryboard = {
  column: number;
  columns: number;
  row: number;
  rows: number;
  storyboardHeight: number;
  storyboardWidth: number;
  storyboardUrl: string;
  thumbnailHeight: number;
  thumbnailWidth: number;
  hasLoaded?: boolean;
};

export type RewindStillMap = Map<
  string,
  {
    storyboards: Array<RewindsStillStoryboard> | undefined;
  }
>;

export type PlaylistStillsMap = Map<string, RewindStillMap>;

export const stillsResolutionConfig = {
  resolutions: ['small', 'extra_large'],
  preloadResolutions: ['small', 'extra_large'],
} as StillsResolutionConfig;

const convertStillTimeToTimestamp = (stillTimeSeconds: number, rewindStartTimestamp: Date): Date =>
  new Date(rewindStartTimestamp.getTime() + Math.round(stillTimeSeconds) * 1000);

const createMidpointsArray = (stills: RewindStill[], rewindStartTimestamp: Date): Date[] =>
  stills.map((still, index) => {
    const isLastStill = index === stills.length - 1;

    if (isLastStill) {
      return convertStillTimeToTimestamp(still.time, rewindStartTimestamp);
    }

    const midpoint = (still.time + stills[index + 1].time) / 2;
    return convertStillTimeToTimestamp(midpoint, rewindStartTimestamp);
  }, []);

const getStoryboardsForStill = (
  baseUrl: string,
  stillIndex: number,
  storyboards: PlayerStills['storyboards'],
): Array<RewindsStillStoryboard> | undefined => {
  if (storyboards?.length > 0) {
    return storyboards.map((storyboard) => {
      const numImagesPerRow = storyboard.columns;
      const storyboardSize = storyboard.columns * storyboard.rows;
      const storyboardFileIndex = Math.floor(stillIndex / storyboardSize);
      return {
        column: stillIndex % numImagesPerRow,
        columns: storyboard.columns,
        row: Math.floor((stillIndex - storyboardFileIndex * storyboardSize) / numImagesPerRow),
        rows: storyboard.rows,
        storyboardHeight: storyboard.storyboardHeight,
        storyboardWidth: storyboard.storyboardWidth,
        storyboardUrl: `${baseUrl}${storyboard.images[storyboardFileIndex]}`,
        thumbnailHeight: storyboard.thumbnailHeight,
        thumbnailWidth: storyboard.thumbnailWidth,
      };
    });
  }
  return undefined;
};

const timestampRoundedToNearestSecond = (timestamp: number | Date | string) => {
  const startTimestamp = new Date(timestamp).getTime();
  return new Date(Math.round(startTimestamp / 1000) * 1000);
};

/**
 * @description This function adds a still for every second of the rewind recording
 * to the stillsMap. It uses the still closest to the given second.
 *
 * The keys in the map are the UTC ISO string that the still represents, rounded to the
 * nearest second. One still can represent multiple seconds.
 *
 * To ensure we use the closest still, we create an array of midpoint times between stills.
 * We then know that each still will represent the time between two midpoints.
 *
 * We sort the stills in the rewind by time, just incase they are not in order (they should be).
 */
export const updateStillsMap = (
  stillsMap: RewindStillMap,
  startDate: string,
  endDate: string,
  playerStills?: PlayerStills,
) => {
  if (!playerStills?.stills) return stillsMap;
  const newStillsMap = new Map(stillsMap.entries());
  const sortedStills = playerStills.stills.sort((a, b) => a.time - b.time);

  const rewindStartRounded = timestampRoundedToNearestSecond(startDate);
  const rewindEndRounded = timestampRoundedToNearestSecond(endDate);

  const midpointsArray = createMidpointsArray(sortedStills || [], rewindStartRounded);

  midpointsArray.forEach((midpoint, index) => {
    const isFirstItemInArray = index === 0;
    const isLastItemInArray = index === midpointsArray.length - 1;

    // the first and last items in the array need to be treated differently as they don't have a previous or next midpoint
    // instead, we use the start or end of the rewind recording
    const stillStartDate = isFirstItemInArray ? rewindStartRounded : midpointsArray[index - 1];
    const stillEndDate = isLastItemInArray ? rewindEndRounded : midpoint;

    // Here we select the still we need and decide how many seconds it should represent (midpoint -> midpoint)
    const stillStartDateSeconds = stillStartDate.getTime() / 1000;
    const stillEndDateSeconds = stillEndDate.getTime() / 1000;
    const numberOfSecondsForStill = stillEndDateSeconds - stillStartDateSeconds;

    let storyboardsForStill: Array<RewindsStillStoryboard> | undefined;

    if (stillsResolutionConfig.resolutions.length && playerStills.storyboards_sized !== undefined) {
      storyboardsForStill = getStoryboardsForStill(
        playerStills?.baseUrl,
        index,
        stillsResolutionConfig.resolutions
          .map((resolution) => playerStills.storyboards_sized?.[resolution])
          .filter((storyboard) => storyboard !== undefined) as Storyboard[],
      );
    }

    // this just iterates over the number of seconds the still is shown for and adds the same
    // still url to the map for all those seconds
    [...Array(numberOfSecondsForStill)].forEach((_, seconds) => {
      const date = new Date((stillStartDateSeconds + seconds) * 1000);
      newStillsMap.set(date.toISOString(), {
        storyboards: storyboardsForStill,
      });
    });
  });

  return newStillsMap;
};
